import Swiper from 'swiper/bundle';
import 'swiper/swiper-bundle.css';

/** Slides a marquee needs to keep moving across even a full-width strip. */
const MARQUEE_MIN_SLIDES = 16;

/** Milliseconds per logo when the block has no speed set. */
const MARQUEE_DEFAULT_SPEED = 7000;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/* Material Symbols Outlined's pause and play_arrow, the same glyphs as the
 * cover video control. Inline so the control doesn't depend on the icon font
 * loading, which it doesn't in the editor canvas. */
const ICON_PAUSE =
  '<svg viewBox="0 -960 960 960" width="24" height="24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M520-200v-560h240v560H520Zm-320 0v-560h240v560H200Zm400-80h80v-400h-80v400Zm-320 0h80v-400h-80v400Z"/></svg>';
const ICON_PLAY =
  '<svg viewBox="0 -960 960 960" width="24" height="24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M320-200v-560l440 280-440 280Zm80-146 210-134-210-134v268Z"/></svg>';

/**
 * Repeat the logos until the marquee has enough slides to scroll.
 *
 * Marquee slides are as wide as their logos, so a short list (five logos)
 * fits inside the strip with nothing left to scroll. The track gets whole sets
 * of logos, because the animation loops by shifting exactly one set. The
 * repeats are hidden from assistive technology and skipped by keyboard focus,
 * so each logo is still announced once.
 *
 * @returns {number} How many sets of logos the track now holds; 0 if none.
 */
const fillMarqueeTrack = (viewport) => {
  const track = viewport.querySelector('.o-social-proof-slider__track');
  if (!track) {
    return 0;
  }

  // The editor re-initializes the block; start again from the real logos.
  track.querySelectorAll('[data-marquee-repeat]').forEach((node) => node.remove());

  const originals = Array.from(track.querySelectorAll('.o-social-proof-slider__item'));
  if (!originals.length) {
    return 0;
  }

  const sets = Math.max(2, Math.ceil(MARQUEE_MIN_SLIDES / originals.length));
  const target = originals.length * sets;
  for (let count = originals.length; count < target; count += 1) {
    const repeat = originals[count % originals.length].cloneNode(true);
    repeat.dataset.marqueeRepeat = '1';
    repeat.setAttribute('aria-hidden', 'true');
    repeat.querySelectorAll('a, button').forEach((node) => node.setAttribute('tabindex', '-1'));
    track.appendChild(repeat);
  }

  return sets;
};

/**
 * The pause/play control (#344). Without it the strip moves for as long as the
 * page is open, which fails WCAG 2.2.2 (Pause, Stop, Hide).
 *
 * Added by script, like the cover video's control: without the script there's
 * no animation to stop. The accessible name states the action and changes
 * with state, the same pattern as the Post Slider and cover video controls.
 * Editors can hide it with the block's "Show Pause Button" field.
 *
 * @returns {Function} Updates the button to match the current state.
 */
const addPlaybackControl = (root, viewport) => {
  let button = root.querySelector('[data-social-proof-playback]');

  if (!button) {
    button = root.ownerDocument.createElement('button');
    button.type = 'button';
    button.className = 'o-social-proof-slider__playback';
    button.setAttribute('data-social-proof-playback', '');
    if (viewport.id) {
      button.setAttribute('aria-controls', viewport.id);
    }
    (root.querySelector('.o-social-proof-slider__inner') || root).appendChild(button);
  }

  const sync = () => {
    const paused = root.classList.contains('is-paused');

    button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    button.setAttribute(
      'aria-label',
      paused ? 'Play the logo animation' : 'Pause the logo animation'
    );
    button.innerHTML = paused ? ICON_PLAY : ICON_PAUSE;
  };

  button.addEventListener('click', () => {
    root.classList.toggle('is-paused');
    // An explicit choice outranks a later change to the reduced-motion setting.
    root.dataset.socialProofPlaybackChosen = '1';
    sync();
  });

  return sync;
};

/**
 * Visitors who ask for less motion get a still strip until they press play,
 * whether or not the block shows the button. Turning the setting on mid-visit
 * pauses it too, unless the visitor already chose with the button.
 */
const respectReducedMotion = (root, onChange) => {
  const view = root.ownerDocument.defaultView || window;
  const motionQuery =
    typeof view.matchMedia === 'function' ? view.matchMedia(REDUCED_MOTION_QUERY) : null;

  if (!motionQuery) {
    return;
  }

  if (motionQuery.matches) {
    root.classList.add('is-paused');
  }

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', (event) => {
      if (event.matches && !root.dataset.socialProofPlaybackChosen) {
        root.classList.add('is-paused');
        onChange();
      }
    });
  }
};

/**
 * Run the marquee as a CSS animation (social-proof-slider.scss), on the site
 * and in the block editor alike.
 *
 * Swiper used to drive it on the site, but it couldn't pause in place: its
 * autoplay stops only after the current slide's transition ends, up to
 * `speed` ms later. Nor can Swiper run in the editor's canvas iframe: its
 * mount() tests `el instanceof HTMLElement` against the parent window (see
 * renderStaticPreview in PostSlider.js). The animation shifts the track by one
 * set of logos per loop, at `speed` ms per logo, and pausing freezes it on the
 * current frame.
 */
const renderMarquee = (root, viewport) => {
  const speed = Number.parseInt(root.dataset.socialProofSpeed || '', 10);
  const logoCount = root.querySelectorAll(
    '.o-social-proof-slider__item:not([data-marquee-repeat])'
  ).length;
  const marqueeHash = `${logoCount}:${speed}`;

  // The editor calls this on every selection change; restarting would jump.
  if (
    root.classList.contains('is-marquee-animated') &&
    root.dataset.socialProofMarqueeHash === marqueeHash
  ) {
    return;
  }

  const sets = fillMarqueeTrack(viewport);
  if (!sets) {
    return;
  }

  root.dataset.socialProofMarqueeHash = marqueeHash;
  root.style.setProperty('--social-proof-marquee-sets', String(sets));
  root.style.setProperty(
    '--social-proof-marquee-duration',
    `${logoCount * (Number.isFinite(speed) && speed > 0 ? speed : MARQUEE_DEFAULT_SPEED)}ms`
  );
  root.classList.toggle('is-pause-on-hover', root.dataset.socialProofPauseOnHover !== '0');
  root.classList.add('is-marquee-animated');

  // Once per element: the editor calls this again when the speed changes.
  if (!root.dataset.socialProofPlaybackReady) {
    root.dataset.socialProofPlaybackReady = '1';

    const sync =
      root.dataset.socialProofShowPause !== '0' ? addPlaybackControl(root, viewport) : () => {};
    respectReducedMotion(root, sync);
    sync();
  }
};

/**
 * @param {Document|Element} scope  Where to look for sliders. In the block
 *   editor this is the canvas iframe's document, not the page's.
 * @param {{editorPreview?: boolean}} options  `editorPreview: true` leaves
 *   carousel mode as a static strip, because Swiper can't run in the editor
 *   canvas. Marquee mode runs the same way everywhere; see renderMarquee.
 */
const SocialProofSlider = (scope = document, { editorPreview = false } = {}) => {
  const rootNode = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
  const sliders = Array.from(rootNode.querySelectorAll('[data-social-proof-slider]'));
  if (!sliders.length) {
    return;
  }

  sliders.forEach((root) => {
    const viewport = root.querySelector('.o-social-proof-slider__viewport');
    const slides = Array.from(root.querySelectorAll('.o-social-proof-slider__item'));
    const prevButton = root.querySelector('[data-social-proof-slider-prev]');
    const nextButton = root.querySelector('[data-social-proof-slider-next]');
    const speed = Number.parseInt(root.dataset.socialProofSpeed || '380', 10);
    const autoplayEnabled = root.dataset.socialProofAutoplay === '1';
    const autoplayDelay = Number.parseInt(root.dataset.socialProofAutoplayDelay || '4000', 10);
    const slidesMobile = Number.parseInt(root.dataset.socialProofSlidesMobile || '2', 10);
    const slidesTablet = Number.parseInt(root.dataset.socialProofSlidesTablet || '3', 10);
    const slidesDesktop = Number.parseInt(root.dataset.socialProofSlidesDesktop || '5', 10);

    if (!viewport || slides.length <= 1) {
      return;
    }

    if (root.dataset.socialProofMarquee === '1') {
      renderMarquee(root, viewport);
      return;
    }

    if (editorPreview) {
      return;
    }

    const baseConfig = {
      slidesPerView: Number.isFinite(slidesMobile) && slidesMobile > 0 ? slidesMobile : 2,
      spaceBetween: 16,
      speed: Number.isFinite(speed) && speed > 0 ? speed : 380,
      loop: true,
      watchOverflow: true,
      keyboard: {
        enabled: true,
      },
      autoplay: autoplayEnabled
        ? {
            delay: Number.isFinite(autoplayDelay) && autoplayDelay > 0 ? autoplayDelay : 4000,
          }
        : false,
      breakpoints: {
        640: {
          slidesPerView: Number.isFinite(slidesTablet) && slidesTablet > 0 ? slidesTablet : 3,
          spaceBetween: 24,
        },
        992: {
          slidesPerView: Number.isFinite(slidesDesktop) && slidesDesktop > 0 ? slidesDesktop : 5,
          spaceBetween: 32,
        },
      },
      navigation:
        prevButton && nextButton
          ? {
              prevEl: prevButton,
              nextEl: nextButton,
            }
          : false,
      a11y: {
        prevSlideMessage: 'Previous brand logos',
        nextSlideMessage: 'Next brand logos',
      },
    };

    const configHash = JSON.stringify({
      speed: baseConfig.speed,
      autoplay: !!baseConfig.autoplay,
      autoplayDelay:
        baseConfig.autoplay && baseConfig.autoplay.delay ? baseConfig.autoplay.delay : 0,
      slidesMobile: baseConfig.slidesPerView,
      slidesTablet: baseConfig.breakpoints[640].slidesPerView,
      slidesDesktop: baseConfig.breakpoints[992].slidesPerView,
      hasNavigation: !!baseConfig.navigation,
    });

    if (viewport.dataset.socialProofInitHash === configHash && viewport.swiper) {
      return;
    }

    if (viewport.swiper && typeof viewport.swiper.destroy === 'function') {
      viewport.swiper.destroy(true, true);
    }

    viewport.dataset.socialProofInitHash = configHash;

    new Swiper(viewport, baseConfig);
  });
};

export default SocialProofSlider;
